/**
 * Single source of truth for every route of purchase-web's MARKETING-WEBSITE
 * portal (`www.purchasepuffin.com`).
 *
 * WHY THIS IS ONE OF THREE REGISTRIES IN THIS APP (PORTAL-ROUTE-REGISTRIES-001)
 * ----------------------------------------------------------------------------
 * purchase-web serves THREE routers on three different hosts, chosen by
 * `src/main.jsx` from the hostname, and they never coexist in one browser:
 *
 *   • `portals/marketing-website` — this one, `www.purchasepuffin.com`.
 *   • `portals/storefront`        — `www.<vanity>.com`, `PURCHASE_STOREFRONT_ROUTES`.
 *   • `portals/owner-console`     — `console.<vanity>`, `OWNER_CONSOLE_ROUTES` (in
 *     `@puffin-core/owner-console`, beside the router it describes).
 *
 * 🔴 So `/products`, `/cart`, `/orders` and `/account` are NOT missing from this
 * registry — they belong to the storefront router and must never be named here.
 * A marketing constant naming one would point a visitor at a path this router
 * has never declared, on a host that could not serve it; the portal contract's
 * "names no path only the TENANT router declares" check is the same idea one
 * boundary over.
 *
 * WHERE THE ROUTER DECLARES THESE
 * -------------------------------
 * `MarketingWebsiteApp.jsx` passes a `routes: [{ path, element }]` ARRAY to
 * `@puffin-core/marketing-shell`'s `createMarketingApp` — a config table, not
 * `<Route>` JSX — and spreads the four blog legs in from that package's
 * `BLOG_TEMPLATE_ROUTES` SSOT (BLOG-STD-001).
 *
 * The route DECLARATIONS stay literal in that array on purpose: making them
 * symbolic would blind the ecosystem's route resolver to the very routes it
 * exists to read.
 *
 * Adding a route:
 *   1. Add `{ path: '/x', element: <XPage /> }` to `MarketingWebsiteApp.jsx`.
 *   2. Add the same `KEY: '/x'` here.
 *   3. Reference it via this registry from every link.
 *
 * @module portals/marketing-website/constants/purchaseMarketingRoutePaths
 */

/**
 * Canonical marketing-website route paths.
 *
 * `HOME` is `'/'` and that is deliberate: unlike the tenant app — where `/` is a
 * redirect source and therefore NOT a namable destination — this router mounts
 * the landing page AT `/`. It is a real page, the 404's first quick link, the
 * contact page's "back" button, and the target the footer's How-It-Works entry
 * hangs its anchor off.
 *
 * Only the parameterless `/blog` index is named. The four `:slug` / `:category`
 * / `:tag` / `:page` patterns come from `@puffin-core/marketing-shell`'s
 * `BLOG_TEMPLATE_ROUTES` (BLOG-STD-001) and are linked from inside that package,
 * never by hand from this app — so naming them here would create constants no
 * file references, which the registry's own orphan check would fail.
 *
 * `LOGIN` is the MKT-LOGIN-XDOMAIN-001 cross-domain login surface, where a
 * tenant admin enters their subdomain and is redirected to their own host. It is
 * not the tenant SPA's `/login`, which lives on a different origin.
 */
export const PURCHASE_MARKETING_ROUTES = Object.freeze({
  HOME: '/',
  FEATURES: '/features',
  PRICING: '/pricing',
  CONTACT: '/contact',
  BLOG: '/blog',
  PRIVACY: '/privacy',
  TERMS: '/terms',
  LOGIN: '/login',
});

/**
 * `#section` anchor ids on the landing page — the OTHER deep-link grain.
 *
 * 🔴 WHY THESE ARE NOT REGISTRY VALUES. A registry value is a ROUTE, and both
 * the shared `createRouteRegistryTest` floor and the cross-app portal contract
 * assert no value carries a `?` or `#`: a hash does not change which route
 * matches, so folding one into the route map would make the registry claim a
 * destination the router cannot be checked against. `PLANNER_ANCHORS` in
 * `apps/planner-web/src/constants/plannerRoutePaths.js` is the precedent this
 * follows.
 *
 * An id here is a TWO-ENDED contract: the section pins it
 * (`<section id={PURCHASE_MARKETING_ANCHORS.HOW_IT_WORKS}>`) and every link
 * names the same constant, so a rename cannot leave one end pointing at
 * nothing. Before this, the footer's `to: '/#how-it-works'`, the hero's
 * `href="#how-it-works"` and the section's `id="how-it-works"` were three
 * independent strings — a rename of the section would have silently turned both
 * links into no-ops (a same-page hash that matches nothing does not 404, it just
 * does nothing, which is why this class survives every navigation audit).
 *
 * ⚠️ `FEATURES` here is the landing page's `#features` SECTION, not the
 * `/features` ROUTE. Both exist and they are different destinations: the CTA's
 * "see features" scrolls down the landing page, the nav's goes to the page.
 */
export const PURCHASE_MARKETING_ANCHORS = Object.freeze({
  HOW_IT_WORKS: 'how-it-works',
  FEATURES: 'features',
});

/**
 * The landing page, landed ON one of its sections.
 *
 * ROUTE-CONCAT-001 permits a `#hash` tail precisely because it cannot change
 * which route matches — appending a path SEGMENT is what ships 404s, and that
 * is what `web/no-route-suffix-concatenation` bans. Built here rather than at
 * the callsite so the `HOME` half comes from the route registry and the anchor
 * half from the anchor map, with no literal at either end.
 *
 * @param {string} anchor - A member of `PURCHASE_MARKETING_ANCHORS`
 * @returns {string} `/#<anchor>`
 */
export function homeAnchorPath(anchor) {
  return `${PURCHASE_MARKETING_ROUTES.HOME}#${anchor}`;
}
